/**
 * Connections · Services (`/services`): every connector grouped by what the
 * reader owes it. Needs you (needs attention, pending setup), Your accounts
 * (the reader's own grants), Connected, Ready to connect, Early access, and a
 * dashed line naming what is planned with the freeform ask beside it. A row
 * opens the connector in the sheet; the sheet's footer is where it connects,
 * reconnects, syncs or disconnects.
 */
import { useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import type { AuthSessionResponse } from "@yagni/shared";
import { RepoPickerModal } from "../components/RepoPickerModal.js";
import { useCan } from "../hooks/useWorkspaceRole.js";
import { oauthInstallUrl } from "../lib/oauthInstallUrls.js";
import { Btn, Group, Groups, Row } from "../queue/index.js";
import { SlackPostInstallConfirmation } from "../settings/integrations/SlackPostInstallConfirmation.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import type { ConnectionsTabProps } from "./ConnectionsPage.js";
import { ConnectorMark } from "./ConnectorMark.js";
import { ConnectorSheet } from "./ConnectorSheet.js";
import { ReviewAppInstallNote } from "./ReviewAppInstallNote.js";
import { STATUS_HUE, STATUS_WORD, groupConnectors, healthLine, keyOf, rowVerb, type ConnectorRecord } from "./connectorsModel.js";
import { useConnections } from "./useConnections.js";

export function ConnectionsTab({ refreshKey }: ConnectionsTabProps) {
  const c = useConnections(refreshKey);
  const canManage = useCan("manage-connections");
  const [searchParams] = useSearchParams();
  const outlet = useOutletContext<{ session?: AuthSessionResponse | null } | null>();
  const [openId, setOpenId] = useState<string | null>(null);
  const [ask, setAsk] = useState("");

  const open = openId ? c.records.find((record) => record.id === openId) ?? null : null;
  useOwnedSheet(
    open ? (
      <ConnectorSheet key={open.id} record={open} c={c} canManage={canManage} onClose={() => setOpenId(null)} onOpen={setOpenId} />
    ) : null,
    () => setOpenId(null),
  );

  const groups = groupConnectors(c.records);
  const row = (record: ConnectorRecord) => {
    const requested = record.kind === "request" && Boolean(c.requested[record.item.id]);
    return (
      <Row
        key={record.id}
        glyph={<ConnectorMark record={record} />}
        rowKey={keyOf(record)}
        title={record.name}
        tag={record.kind === "request" ? undefined : { label: STATUS_WORD[record.status], color: STATUS_HUE[record.status] }}
        state={healthLine(record, { repoCount: c.repoCount })}
        verb={rowVerb(record, requested)}
        sel={record.id === openId}
        onOpen={() => setOpenId(record.id)}
        className={`connector-${record.kind}`}
      />
    );
  };

  const workspaceError = searchParams.get("error") === "workspace_required";
  const justInstalledSlack = searchParams.get("justInstalled") === "slack";

  return (
    <Groups>
      {c.error && !open && (
        <div className="qnote err" role="alert">
          <span>{c.error}</span>
        </div>
      )}
      {c.statusError && (
        <div className="qnote err">
          <span>{c.statusError}</span>
        </div>
      )}
      {c.notices.map((notice) => (
        <div
          key={notice.key}
          className={`qnote ${notice.tone === "error" ? "err" : "info"}`}
          data-testid={notice.key === "jira" ? "jira-connect-notice" : notice.key === "github" ? "github-install-notice" : `${notice.key}-notice`}
        >
          <span>{notice.message}</span>
          <span className="acts">
            <Btn size="sm" variant="quiet" onClick={() => c.dismissNotice(notice.key)}>
              Dismiss
            </Btn>
          </span>
          {notice.jiraSites && notice.jiraSites.length > 0 && (
            <span className="acts" data-testid="jira-site-picker">
              {notice.jiraSites.map((site) => (
                <a
                  key={site.id}
                  className="btn secondary sm"
                  href={oauthInstallUrl("jira", "/services", { site: site.id })}
                  data-testid={`jira-site-${site.id}`}
                >
                  {site.name}
                  {site.url ? <span className="fact"> {site.url.replace(/^https?:\/\//, "")}</span> : null}
                </a>
              ))}
            </span>
          )}
        </div>
      ))}
      {c.githubPendingInstall && (
        <div className="qnote info" data-testid="github-pending-install">
          <span>
            GitHub reports the YAGNI App was installed on <b>{c.githubPendingInstall.account}</b>. Connect it to the{" "}
            <b>{outlet?.session?.workspace?.name ?? "current"}</b> workspace? If this belongs to a different workspace,
            switch to that workspace first and use Link existing there.
          </span>
          <span className="acts">
            <Btn size="sm" variant="primary" disabled={c.githubPendingBusy || !canManage} onClick={() => void c.confirmGithubPendingInstall()}>
              {c.githubPendingBusy ? "Opening GitHub..." : "Connect to this workspace"}
            </Btn>
            <Btn size="sm" variant="quiet" disabled={c.githubPendingBusy} onClick={c.dismissGithubPendingInstall}>
              Dismiss
            </Btn>
          </span>
        </div>
      )}
      <ReviewAppInstallNote repos={null} />
      {justInstalledSlack && <SlackPostInstallConfirmation />}
      {workspaceError && (
        <div className="qnote err">
          <span>
            <b>Personal Gmail accounts aren't supported.</b> YAGNI needs a Google Workspace account.{" "}
            <a href="https://workspace.google.com/" target="_blank" rel="noopener noreferrer">
              Learn about Google Workspace
            </a>
          </span>
        </div>
      )}

      <Group
        label="Needs you"
        tone="needs"
        empty={c.loading && c.records.length === 0 ? "Reading your connections." : "Every connection is healthy."}
      >
        {groups.needs.map(row)}
      </Group>
      <Group label="Your accounts" plain>
        {groups.yours.map(row)}
      </Group>
      <Group label="Connected" plain>
        {groups.connected.map(row)}
      </Group>
      <Group label="Ready to connect" tone="done">
        {groups.ready.map(row)}
      </Group>
      <Group label="Early access" tone="done">
        {groups.early.map(row)}
      </Group>

      <form
        className="qline dashed"
        data-testid="connector-request-line"
        onSubmit={(event) => {
          event.preventDefault();
          void c.requestFreeform(ask).then((ok) => ok && setAsk(""));
        }}
      >
        <span>
          {groups.planned.length > 0 && (
            <>
              Planned:{" "}
              {groups.planned.map((record, index) => (
                <span key={record.id}>
                  {index > 0 ? ", " : ""}
                  <button type="button" className="nm" onClick={() => setOpenId(record.id)}>
                    {record.name}
                  </button>
                </span>
              ))}
              .{" "}
            </>
          )}
          {c.freeformDone ? `Got it: "${c.freeformDone}" is on the request list.` : "Don't see your tool? Name it."}
        </span>
        <span className="acts">
          <input
            className="in"
            value={ask}
            onChange={(event) => setAsk(event.target.value)}
            placeholder="The tool's name"
            aria-label="Request another connector"
          />
          <Btn type="submit" variant="quiet" size="sm" disabled={Boolean(c.busy.freeform) || ask.trim().length < 2}>
            Request
          </Btn>
        </span>
      </form>

      <RepoPickerModal open={c.repoPickerOpen} onClose={() => c.setRepoPickerOpen(false)} onReposChanged={c.loadRepoCount} />
    </Groups>
  );
}
